import type { BenefitDetailDto, BenefitGrantDto, BenefitWorkflowResponse } from "./benefit-types"
import { actionableGrantReview, usableGrantAuthority } from "./grant-workspace"

/** Captured payment prerequisites, never a replacement for the current saved workspace. */
export interface VendorPaymentContext {
  benefitId: string
  concurrencyToken: string
  authority: "Legacy" | "V2"
  grant: BenefitGrantDto
  generationId: string | null
  reviewVersion: number | null
}

export function captureVendorPaymentContext(benefit: BenefitDetailDto, grant: BenefitGrantDto, workflow: BenefitWorkflowResponse | null): VendorPaymentContext | null {
  if (!benefit.concurrencyToken) return null
  const instance = grant.benefitProgramInstance
  const grants = benefit.benefitGrants.filter(row => row.benefitProgramInstance.program === instance.program && row.benefitProgramInstance.year === instance.year)
  // Both endpoints address vendor IDs, not array positions. Ambiguous rows cannot be edited.
  if (grants.length !== 1 || JSON.stringify(grants[0]) !== JSON.stringify(grant) ||
    new Set(grant.serviceProviderPayments.map(payment => payment.vendorId)).size !== grant.serviceProviderPayments.length) return null
  const legacy = workflow?.authority === "Legacy" && workflow.display?.authority === "Legacy" && workflow.display.readUnavailable === false
  const reviews = usableGrantAuthority(workflow) ? workflow.grantReviews.filter(review => review.programId === instance.program && review.programYear === instance.year) : []
  const review = reviews.length === 1 ? reviews[0] : null
  if (!legacy && !actionableGrantReview(review)) return null
  return { benefitId: benefit.id, concurrencyToken: benefit.concurrencyToken, authority: legacy ? "Legacy" : "V2",
    grant: structuredClone(grant), generationId: review?.generationId ?? null, reviewVersion: review?.version ?? null }
}

/** Equality only: missing/duplicate rows, unversioned payment changes and authority changes fail closed. */
export function vendorPaymentContextCurrent(context: VendorPaymentContext | null, benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null): boolean {
  if (!context || benefit.id !== context.benefitId || benefit.concurrencyToken !== context.concurrencyToken) return false
  const current = captureVendorPaymentContext(benefit, context.grant, workflow)
  return !!current && current.authority === context.authority && current.generationId === context.generationId && current.reviewVersion === context.reviewVersion
}
